// El árbol de categorías de la marca, cargado una vez por pantalla —
// ADR-2026-09-07-categorias-de-tienda.
//
// Vive acá y no en `LanzamientoDetalle` porque lo usan dos cosas a la vez: el
// selector de cada ficha y el panel para armarlo. Con el estado en cada una,
// crear una categoría desde el panel no la haría aparecer en el selector hasta
// recargar la pantalla.
//
// Las escrituras recargan el árbol entero en vez de aplicar el cambio a mano: la
// lista es chica (el tope de Tiendanube son 1000 y una tienda real tiene
// decenas), y borrar en cascada o mover una rama cambia tantas filas que
// reproducirlo en memoria sería reimplementar el `on delete cascade`.
import { useCallback, useEffect, useState } from "react";
import {
  borrarCategoria,
  crearCategoria,
  fetchCategorias,
  moverCategoria,
  renombrarCategoria,
  type CategoriaDeTienda,
} from "./lanzamientosApi";

export function useCategorias(companyId: string | null, brandId: string | null) {
  const [categorias, setCategorias] = useState<CategoriaDeTienda[]>([]);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    if (!companyId) {
      setCategorias([]);
      setCargando(false);
      return;
    }
    setCategorias(await fetchCategorias(companyId, brandId));
    setCargando(false);
  }, [companyId, brandId]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  /**
   * Crear devuelve el id además del error: para armar un camino
   * ("Remeras › Oversize") hay que saber qué id quedó para colgarle la siguiente.
   * El resto de las acciones devuelven solo el error.
   */
  const crear = useCallback(
    async (
      nombre: string,
      madre: string | null,
    ): Promise<{ id: string | null; error: string | null }> => {
      if (!companyId) return { id: null, error: "No se pudo crear la categoría" };
      const r = await crearCategoria(companyId, brandId, nombre, madre);
      if (!r.error) await cargar();
      return r;
    },
    [companyId, brandId, cargar],
  );

  const renombrar = useCallback(
    async (id: string, nombre: string): Promise<string | null> => {
      const r = await renombrarCategoria(id, nombre);
      if (!r.error) await cargar();
      return r.error;
    },
    [cargar],
  );

  const mover = useCallback(
    async (id: string, parentId: string | null): Promise<string | null> => {
      const r = await moverCategoria(id, parentId);
      if (!r.error) await cargar();
      return r.error;
    },
    [cargar],
  );

  const borrar = useCallback(
    async (id: string): Promise<string | null> => {
      const r = await borrarCategoria(id);
      if (!r.error) await cargar();
      return r.error;
    },
    [cargar],
  );

  return { categorias, cargando, cargar, crear, renombrar, mover, borrar };
}
